import { format } from 'date-fns/format';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { createStructuredSelector } from 'reselect';
import { SCButton, SCLoader } from 'rollup-allspark';
import {
  EXPORT_TYPE,
  NATIVE_ANALYTICS_SAGA,
  OOS_TABLE_TYPE,
  OOS_TABS,
} from '../../../../config/constants/analytics';
import SectionCard from '../../components/SectionCard';
import ChartTabs from '../../components/ChartTabs';
import useAnalytics from '../../../../hooks/useAnalytics';
import { isEmpty } from '../../../../utils';
import { generateXLSXDataFromCSVData } from '../../../../utils/nativeAnalyticsUtils';
import { convertToHoursAndMinutes } from '../../../../utils/numToTime';
import {
  downloadCsvFromJSON,
  downloadXLSXFromJSON,
} from '../../../../utils/sheetUtils';
import { getList } from '../../actions';
import { makeSelectProperty } from '../../selectors';
import OosTable from './OosTable';

const OosTableCard = ({
  selectedBusiness,
  selectedBranch,
  selectedBrand,
  itemReportData,
  itemLogData,
  modifierReportData,
  modifierLogData,
  listLoading,
  handleGetList,
}) => {
  // CONSTANTS

  // HOOKS
  const [selectedTab, setSelectedTab] = useState(OOS_TABS[0]);
  const [tableData, setTableData] = useState(null);
  const [tableHeaders, setTableHeaders] = useState(null);
  const [currentTableType, setCurrentTableType] = useState(
    OOS_TABLE_TYPE.ITEMS.title,
  );
  const [activeDownloadKey, setActiveDownloadKey] = useState(null);

  const { getAnalyticsParams } = useAnalytics();

  // HANDLERS
  useEffect(() => {
    setSelectedTab(OOS_TABS[0]);
    setCurrentTableType(OOS_TABLE_TYPE.ITEMS.title);
  }, []);

  // GET Table Data
  const getKeys = (tabType, modeTitle) => {
    if (tabType === 'analytics') {
      if (modeTitle === OOS_TABLE_TYPE.ITEMS.title) {
        return { urlKey: 'OOS_REPORT_ITEM', reducerKey: 'itemReportData' };
      }
      return {
        urlKey: 'OOS_REPORT_MODIFIER',
        reducerKey: 'modifierReportData',
      };
    }
    if (modeTitle === OOS_TABLE_TYPE.MODIFIERS.title) {
      return { urlKey: 'OOS_LOGS_MODIFIER', reducerKey: 'modifierLogData' };
    }
    return { urlKey: 'OOS_LOGS_ITEM', reducerKey: 'itemLogData' };
  };

  const fetchData = (tab, modeTitle) => {
    if (isEmpty(selectedBusiness)) return;

    const params = { ...getAnalyticsParams() };
    const { urlKey, reducerKey } = getKeys(tab.type, modeTitle);
    const url = NATIVE_ANALYTICS_SAGA[urlKey]?.GET;

    if (url) {
      handleGetList({
        url,
        params,
        reducerKey,
      });
    }
  };
  // Table Type and Tab
  const handleChangeTab = tab => {
    if (isEmpty(selectedBusiness)) return;

    setSelectedTab(tab);
    setCurrentTableType(OOS_TABLE_TYPE.ITEMS.title);

    fetchData(tab, OOS_TABLE_TYPE.ITEMS.title);
  };
  const handleTableTypeChange = modeTitle => () => {
    if (isEmpty(selectedBusiness)) return;
    setCurrentTableType(modeTitle);

    fetchData(selectedTab, modeTitle);
  };

  // TABLE HEADERS DATA GENERATOR
  const generateTableHeaders = additionalHeaders => {
    const commonHeaders = [
      {
        slug: `${currentTableType === 'item' ? 'title' : 'modifier_title'}`,
        title: `${currentTableType === 'item' ? 'Item' : 'Modifier'} Name`,
      },
      { slug: 'branch_title', title: 'Branch' },
      { slug: 'brand_title', title: 'Brand' },
      { slug: 'duration', title: 'Total OOS Time' },
    ];

    const mergedHeaders = [...commonHeaders];

    if (additionalHeaders && additionalHeaders.length > 0) {
      mergedHeaders.splice(1, 0, ...additionalHeaders);
    }

    return mergedHeaders;
  };

  const REPORT_ITEMS_TABLE_HEADERS = generateTableHeaders();
  const REPORT_MODIFIERS_TABLE_HEADERS = generateTableHeaders([
    { slug: 'modifier_group_title', title: 'Modifier Group' },
  ]);
  const LOGS_ITEMS_TABLE_HEADERS = generateTableHeaders([
    { slug: 'start_time', title: 'Start Time' },
    { slug: 'end_time', title: 'End Time' },
  ]);
  const LOGS_MODIFIERS_TABLE_HEADERS = generateTableHeaders([
    { slug: 'modifier_group_title', title: 'Modifier Group' },
    { slug: 'start_time', title: 'Start Time' },
    { slug: 'end_time', title: 'End Time' },
  ]);

  // TABLE ROW DATA GENERATOR
  function generateTableBody(data = []) {
    if (isEmpty(data)) return [];
    // sort these by durations first
    // then for same duration, sort by title
    const sortedData = [...data].sort((a, b) => {
      if (a?.oos_mins !== b?.oos_mins) {
        return b.oos_mins - a.oos_mins;
      }
      if (!isEmpty(a?.modifier_id)) {
        return a?.modifier_title?.localeCompare(b?.modifier_title);
      }
      return a?.title?.localeCompare(b?.title);
    });
    return sortedData?.map(item => {
      const matchedBranch = selectedBranch.find(
        branch => branch.id === item.branch_id,
      );
      const matchedBrand = selectedBrand.find(
        brand => brand.id === item.brand_id,
      );

      return {
        ...item,
        branch_title: matchedBranch ? matchedBranch.title : null,
        brand_title: matchedBrand ? matchedBrand.title : null,
        duration: convertToHoursAndMinutes(item.oos_mins) || '0m',
      };
    });
  }

  useEffect(() => {
    if (selectedTab.type === 'analytics') {
      if (currentTableType === OOS_TABLE_TYPE.ITEMS.title) {
        setTableData(generateTableBody(itemReportData?.analytics));
        setTableHeaders(REPORT_ITEMS_TABLE_HEADERS);
      } else {
        setTableData(generateTableBody(modifierReportData?.analytics));
        setTableHeaders(REPORT_MODIFIERS_TABLE_HEADERS);
      }
    } else if (currentTableType === OOS_TABLE_TYPE.ITEMS.title) {
      setTableData(generateTableBody(itemLogData?.logs));
      setTableHeaders(LOGS_ITEMS_TABLE_HEADERS);
    } else {
      setTableData(generateTableBody(modifierLogData?.logs));
      setTableHeaders(LOGS_MODIFIERS_TABLE_HEADERS);
    }
  }, [itemReportData, itemLogData, modifierReportData, modifierLogData]);

  // CSV DOWNLOAD
  const generateOOSReportCSVData = () => {
    const CSVData = [tableHeaders.map(header => header.title)];

    tableData.forEach(dataRow => {
      const rowData = tableHeaders.map(header => dataRow[header.slug]);
      CSVData.push(rowData);
    });

    return CSVData;
  };

  const handleCSVDownload = () => {
    setActiveDownloadKey(EXPORT_TYPE.CSV);
    try {
      const CSVData = generateOOSReportCSVData();
      downloadCsvFromJSON(
        CSVData,
        `OOS-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
      );
    } finally {
      setActiveDownloadKey(null);
    }
  };

  const handleXLSXDownload = () => {
    setActiveDownloadKey(EXPORT_TYPE.XLSX);
    try {
      const CSVData = generateOOSReportCSVData();
      const xlsxData = generateXLSXDataFromCSVData(CSVData);
      downloadXLSXFromJSON(
        xlsxData,
        `OOS-${format(new Date(), 'yyyy-mm-dd hh:mm')}`,
      );
    } finally {
      setActiveDownloadKey(null);
    }
  };

  const isExportInProgress = Boolean(activeDownloadKey);
  const areExportButtonsDisabled = listLoading || isExportInProgress;
  const isTabClickBlocked = listLoading || isExportInProgress;

  const isViewTabDisabled = tab =>
    isExportInProgress || (listLoading && tab.id !== selectedTab.id);

  const isTypeTabDisabled = tab =>
    isExportInProgress || (listLoading && tab.id !== currentTableType);

  const isExportButtonLoading = exportType => activeDownloadKey === exportType;

  const renderExportButton = exportType => {
    const isLoading = isExportButtonLoading(exportType);
    const label = exportType.toUpperCase();

    return (
      <SCButton
        key={exportType}
        variant="secondary"
        size="sm"
        className="rounded-lg bg-grey-light text-black hover:bg-grey gap-x-1 w-auto flex items-center px-4 h-6 text-xs font-medium"
        action={
          exportType === EXPORT_TYPE.CSV
            ? handleCSVDownload
            : handleXLSXDownload
        }
        disabled={areExportButtonsDisabled}
      >
        <div className="flex items-center gap-x-1 font-medium min-w-[42px] justify-center">
          {isLoading ? (
            <SCLoader lineWidth={2} diameter={16} />
          ) : (
            <>
              <i className="kt-download" />
              <p>{label}</p>
            </>
          )}
        </div>
      </SCButton>
    );
  };

  const renderDownloadActions = !isEmpty(tableData) ? (
    <div className="flex items-center gap-x-2">
      {renderExportButton(EXPORT_TYPE.CSV)}
      {renderExportButton(EXPORT_TYPE.XLSX)}
    </div>
  ) : null;

  // OOS Report / OOS Logs view tabs + Items / Modifiers type tabs
  const oosViewTabs = OOS_TABS.map(t => ({ id: t.id, title: t.title }));
  const oosTypeTabs = Object.values(OOS_TABLE_TYPE).map(t => ({
    id: t.title,
    title: t.label,
  }));

  return (
    <SectionCard title="Out of Stock (OOS)">
      <div className="mb-4">
        <ChartTabs
          tabs={oosViewTabs}
          selected={selectedTab.id}
          onSelect={id => handleChangeTab(OOS_TABS.find(t => t.id === id))}
          isTabDisabled={isViewTabDisabled}
          isSelectionBlocked={isTabClickBlocked}
        />
      </div>
      <div className="mb-4 flex items-center justify-between gap-x-3">
        <ChartTabs
          tabs={oosTypeTabs}
          selected={currentTableType}
          onSelect={id => handleTableTypeChange(id)()}
          isTabDisabled={isTypeTabDisabled}
          isSelectionBlocked={isTabClickBlocked}
        />
        {renderDownloadActions}
      </div>
      <OosTable
        tableHeaderData={tableHeaders}
        tableBodyData={tableData}
        listLoading={listLoading}
        tableType={currentTableType}
        activeTab={selectedTab}
      />
    </SectionCard>
  );
};
OosTableCard.propTypes = {
  selectedBusiness: PropTypes.any,
  selectedBranch: PropTypes.any,
  selectedBrand: PropTypes.any,
  itemReportData: PropTypes.any,
  itemLogData: PropTypes.any,
  modifierReportData: PropTypes.any,
  modifierLogData: PropTypes.any,
  listLoading: PropTypes.bool,
  handleGetList: PropTypes.func,
};
const mapStateToProps = createStructuredSelector({
  selectedBusiness: makeSelectProperty('selectedBusiness'),
  selectedBranch: makeSelectProperty('selectedBranch'),
  selectedBrand: makeSelectProperty('selectedBrand'),
  itemReportData: makeSelectProperty(NATIVE_ANALYTICS_SAGA.OOS_REPORT_ITEM.KEY),
  itemLogData: makeSelectProperty(NATIVE_ANALYTICS_SAGA.OOS_LOGS_ITEM.KEY),
  modifierReportData: makeSelectProperty(
    NATIVE_ANALYTICS_SAGA.OOS_REPORT_MODIFIER.KEY,
  ),
  modifierLogData: makeSelectProperty(
    NATIVE_ANALYTICS_SAGA.OOS_LOGS_MODIFIER.KEY,
  ),
  listLoading: makeSelectProperty('listLoading'),
});

function mapDispatchToProps(dispatch) {
  return {
    handleGetList: params => dispatch(getList(params)),
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
export default compose(withConnect)(OosTableCard);
